import { ApolloError } from '@apollo/client';
import { Address } from '../features/serviceLocation/serviceLocationSlice';
import { coverageAttribution } from './aperioTracking';

type PlaceResult = google.maps.places.PlaceResult;

/** The address fields GET_POSTAL_WITH_ADDRESS sends for the coverage log. */
export interface CheckedAddressFields {
  street: string | null;
  unit: string | null;
  address: string | null;
  placeId: string | null;
  lastSource: string | null;
  lastMedium: string | null;
  lastCampaign: string | null;
  gclid: string | null;
  fbclid: string | null;
  oppref: string | null;
  referrerHost: string | null;
}

/**
 * The address being checked, for Aperio's coverage log (getPostal). It goes
 * to Aperio's own server only - never to Mixpanel, GA4 or an ad tag - and
 * the server keeps it for 24 months.
 *
 * `place` is the Google place the visitor picked in this box (only when the
 * "new address" option is the one in use), which adds the unit, Google's
 * one-line address and its place_id; `handoffPlaceId` is the place_id the
 * marketing site resolved, for a hand-off that arrived already resolved.
 * A saved address has neither, and sends its street alone.
 */
export function checkedAddressFields(
  address?: Address,
  place?: PlaceResult,
  handoffPlaceId?: string | null,
): CheckedAddressFields {
  const unit = place?.address_components?.find(
    (component) => component.types.includes('subpremise'),
  )?.long_name;
  // placeResultToAddress writes "undefined Main St" for a pick with no
  // house number; the log gets the words that are really there.
  const street = String(address?.street || '')
    .replace(/\bundefined\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return {
    street: street || null,
    unit: unit || null,
    address: place?.formatted_address || null,
    placeId: place?.place_id || handoffPlaceId || null,
    ...coverageAttribution(),
  };
}

/**
 * True when the server refused GET_POSTAL_WITH_ADDRESS because it does not
 * know the address fields yet (a deploy where the client landed first):
 * `Field "street" is not defined by type "GetPostalInput"`, as a GraphQL
 * error or inside a 400 response. Anything else is a real failure.
 */
export function addressFieldsRefused(error?: ApolloError | null): boolean {
  if (!error) return false;
  const network = error.networkError as { result?: { errors?: { message?: string }[] } } | null;
  const messages = [
    error.message,
    ...(error.graphQLErrors || []).map((e) => e.message),
    ...((network?.result?.errors) || []).map((e) => e?.message),
  ].map((m) => String(m || ''));
  return messages.some((m) => /GetPostalInput/.test(m)
    && /not defined|Unknown argument|unknown field/i.test(m));
}
